import { auth } from '../config/firebase';
import type { SocialPlatform } from '../components/icons/SocialIcon';

export const executePlatformConnection = async (platform: SocialPlatform) => {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Debug: Log token info
    console.log('🔍 Firebase token length:', token.length);
    console.log('🔍 Firebase token starts with:', token.substring(0, 50) + '...');
    console.log('🔍 Firebase user:', auth.currentUser?.uid);
    console.log('🔍 Firebase user email:', auth.currentUser?.email);

    // Map platform to API endpoint - Facebook uses 'meta' endpoint
    const getApiEndpoint = (platform: SocialPlatform): string => {
      if (platform === 'facebook') {
        return 'meta';
      }
      return platform;
    };

    const formattedPlatform = getApiEndpoint(platform);

    console.log(`Connecting to platform: ${platform}, API endpoint: management/accounts/${formattedPlatform}/config`);

    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };

    // Debug: Log headers
    console.log('🔍 Request headers:', headers);
    console.log('🔍 Authorization header:', headers.Authorization.substring(0, 50) + '...');

    const response = await fetch(`https://api.socialness.ai/management/accounts/${formattedPlatform}/config`, {
      headers
    });

    console.log('🔍 Response status:', response.status);
    console.log('🔍 Response headers:', Object.fromEntries([...response.headers.entries()]));

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Platform configuration request failed with status: ${response.status}`);
      console.error(`Error details:`, errorText);
      throw new Error(`Failed to get platform configuration: ${response.statusText}`);
    }

    const data = await response.json();
    
    if (data.success && data.authUrl) {
      localStorage.setItem('socialness_oauth_pending', 'true');
      localStorage.setItem('socialness_oauth_platform', platform);
      localStorage.setItem('socialness_oauth_timestamp', Date.now().toString());
      
      window.location.href = data.authUrl;
      return true;
    } else {
      throw new Error('Invalid platform configuration response: Missing authUrl');
    }
  } catch (error) {
    console.error('Error connecting to platform:', error);
    let errorMessage = 'Failed to connect to platform. Please try again.';
    if (error instanceof Error) {
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        errorMessage = 'Authentication failed. Please log out and log back in to try again.';
      } else if (error.message.includes('404') || error.message.includes('Not Found')) {
        errorMessage = `The ${platform} connection service is currently unavailable. Please try again later.`;
      } else if (error.message.includes('500')) {
        errorMessage = 'Server error occurred. Our team has been notified. Please try again later.';
      } else if (error.message.includes('Network Error') || error.message.includes('Failed to fetch')) {
        errorMessage = 'Network connection issue. Please check your internet connection and try again.';
      }
      console.error('Detailed error:', error.message);
    }
    throw new Error(errorMessage);
  }
};

export const invalidateAccountsCache = async () => {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      console.error('No authentication token available');
      return;
    }
    console.log('Invalidating accounts cache via API');
    const response = await fetch(`https://api.socialness.ai/management/accounts/invalidate-cache`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    if (!response.ok) {
      console.error('Failed to invalidate cache:', response.status, response.statusText);
      return;
    }
    const data = await response.json();
    console.log('Cache invalidation response:', data);
  } catch (error) {
    console.error('Error invalidating accounts cache:', error);
  }
}; 